import React from 'react';
import { useSelector } from 'react-redux';
import useAuthGateState from '../hooks/useAuthGateState';
import {
  hasCandidateCredential,
  type CredentialState,
  type VerificationAccessFailure,
} from './verificationCredentials';

interface VerificationAccessRetryParams {
  access?: VerificationAccessFailure | null;
  errors: unknown;
  authLoading: boolean;
  verificationId?: string;
}

interface RetryState {
  id?: string;
  earlierFailure: unknown;
  retried: boolean;
}

// An access failure left by an earlier visit (e.g. a 401 before the candidate
// logged in) gets one retry, as does a 401 sent before the login check had a
// credential to send. React Router reuses the page when :verificationId
// changes, so the retry state starts over for each verification.
const useVerificationAccessRetry = ({
  access,
  errors,
  authLoading,
  verificationId,
}: VerificationAccessRetryParams) => {
  // The JWT is read from the auth Context while rendering. Reading it through
  // a Redux selector would never re-run when the login check finds one (the
  // store no longer changes), and the AuthProvider's credential reader is only
  // updated after the render that sees the new state.
  const { candidateLogin } = useAuthGateState();
  const portalToken = useSelector(
    (state: CredentialState) => state.portal?.token,
  );
  const credentialHeld =
    Boolean(candidateLogin.candidateJwt) ||
    hasCandidateCredential({ portal: { token: portalToken } });
  const freshState = (): RetryState => ({
    id: verificationId,
    earlierFailure: access ? errors : null,
    retried: false,
  });
  const [stored, setStored] = React.useState(freshState);
  let current = stored;
  if (stored.id !== verificationId) {
    current = freshState();
    setStored(current);
  }
  const { earlierFailure, retried } = current;
  const retryable =
    !!access &&
    !retried &&
    (errors === earlierFailure ||
      (access.status === 401 && !access.credentialed));
  const waitingToRetry = retryable && authLoading;
  const retryNow =
    retryable && !authLoading && (errors === earlierFailure || credentialHeld);
  const markRetried = React.useCallback(
    () =>
      setStored(state =>
        state.id === verificationId ? { ...state, retried: true } : state,
      ),
    [verificationId],
  );

  return { waitingToRetry, retryNow, markRetried };
};

export default useVerificationAccessRetry;
